/**
 * Diagnostic Scoring Engine
 * 
 * Calculates Diagnostic Competency Scores (DCS) and related metrics
 * for the Diagnostic Intelligence system.
 */

import {
  type DCSCalculationInput,
  type DCSResult,
  type DiagnosticSampleStatus,
  DCS_WEIGHTS,
  SAMPLE_STATUS_THRESHOLDS,
} from '@/types/diagnosticIntelligence';

/**
 * Determine sample status based on diagnostic count
 */
export function getSampleStatus(sampleCount: number): DiagnosticSampleStatus {
  if (sampleCount < SAMPLE_STATUS_THRESHOLDS.learning) return 'learning';
  if (sampleCount < SAMPLE_STATUS_THRESHOLDS.established) return 'established';
  return 'expert';
}

/**
 * Calculate calibration score (confidence vs outcome alignment)
 * 
 * High confidence + correct = good calibration (high score)
 * High confidence + incorrect = overconfidence (low score)
 * Low confidence + correct = underconfidence (medium score)
 * Low confidence + incorrect = appropriate caution (medium score)
 */
export function calculateCalibrationScore(
  avgConfidence: number, // 1-5 scale
  outcomeSuccessRate: number // 0-100
): number {
  // Normalize confidence to 0-100 scale
  const normalizedConfidence = ((avgConfidence - 1) / 4) * 100;
  
  // Perfect calibration = confidence matches outcome rate
  // e.g., 80% confident and 80% success rate = perfect
  const calibrationError = Math.abs(normalizedConfidence - outcomeSuccessRate);
  
  // Convert error to score (0 error = 100 score)
  // Allow some tolerance (up to 15% error still gets decent score)
  const calibrationScore = Math.max(0, 100 - (calibrationError * 1.2));
  
  return Math.round(calibrationScore * 100) / 100;
}

/**
 * Calculate efficiency component based on diagnostic time
 */
export function calculateEfficiencyScore(
  avgDiagnosticMinutes: number,
  expectedAvgMinutes: number
): number {
  if (expectedAvgMinutes <= 0) return 50; // Neutral if no baseline
  
  const ratio = avgDiagnosticMinutes / expectedAvgMinutes;
  
  // Faster than expected = good (up to 100)
  // At expected = 75
  // 50% slower = 50
  // 100% slower = 25
  if (ratio <= 0.7) return 100; // Very efficient
  if (ratio <= 1.0) return 75 + (1 - ratio) * 83.33; // 75-100 range
  if (ratio <= 1.5) return 50 + (1.5 - ratio) * 50; // 50-75 range
  if (ratio <= 2.0) return 25 + (2 - ratio) * 50; // 25-50 range
  return Math.max(0, 25 - (ratio - 2) * 25); // Below 25
}

/**
 * Apply sample size weight to pull scores toward neutral
 * Low sample sizes = pull toward 50 (neutral)
 * High sample sizes = trust the calculated score
 */
function applySampleSizeWeight(score: number, sampleCount: number): number {
  const NEUTRAL_SCORE = 50;
  const FULL_CONFIDENCE_SAMPLES = 30;
  
  // Weight increases from 0 to 1 as samples increase
  const weight = Math.min(1, sampleCount / FULL_CONFIDENCE_SAMPLES);
  
  // Blend between neutral and calculated score
  return NEUTRAL_SCORE + (score - NEUTRAL_SCORE) * weight;
}

/**
 * Calculate Diagnostic Competency Score (DCS)
 * 
 * Weighted combination of:
 * - Calibration (35%): confidence vs outcome alignment
 * - Outcome Success (30%): % of correct diagnoses
 * - Complaint Validation (20%): complaint confirmation accuracy
 * - Efficiency (15%): diagnostic time efficiency
 */
export function calculateDCS(input: DCSCalculationInput): DCSResult {
  const {
    avgConfidence,
    outcomeSuccessRate,
    complaintValidationRate,
    avgDiagnosticMinutes,
    expectedAvgMinutes,
    sampleCount,
  } = input;

  // Calculate individual components
  const calibrationScore = calculateCalibrationScore(avgConfidence, outcomeSuccessRate);
  const efficiencyScore = calculateEfficiencyScore(avgDiagnosticMinutes, expectedAvgMinutes);

  // Calculate weighted components
  const calibrationComponent = calibrationScore * DCS_WEIGHTS.calibration;
  const outcomeComponent = outcomeSuccessRate * DCS_WEIGHTS.outcomeSuccess;
  const complaintComponent = complaintValidationRate * DCS_WEIGHTS.complaintValidation;
  const efficiencyComponent = efficiencyScore * DCS_WEIGHTS.efficiency;

  // Calculate raw DCS
  const rawDCS = calibrationComponent + outcomeComponent + complaintComponent + efficiencyComponent;

  // Apply sample size weighting (pull toward neutral for low samples)
  const diagnosticCompetencyScore = applySampleSizeWeight(rawDCS, sampleCount);

  // Determine sample status
  const sampleStatus = getSampleStatus(sampleCount);

  return {
    diagnosticCompetencyScore: Math.round(diagnosticCompetencyScore * 100) / 100,
    calibrationScore,
    sampleStatus,
    breakdown: {
      calibrationComponent: Math.round(calibrationComponent * 100) / 100,
      outcomeComponent: Math.round(outcomeComponent * 100) / 100,
      complaintComponent: Math.round(complaintComponent * 100) / 100,
      efficiencyComponent: Math.round(efficiencyComponent * 100) / 100,
    },
  };
}

/**
 * Apply EMA (Exponential Moving Average) smoothing for gradual updates
 */
export function applyEMASmoothing(
  currentValue: number,
  newValue: number,
  alpha: number = 0.1
): number {
  return currentValue + alpha * (newValue - currentValue);
}

/**
 * Get display label for sample status
 */
export function getSampleStatusLabel(status: DiagnosticSampleStatus): string {
  switch (status) {
    case 'learning':
      return 'Learning';
    case 'established':
      return 'Established';
    case 'expert':
      return 'Expert';
  }
}

/**
 * Get color class for sample status badge
 */
export function getSampleStatusColor(status: DiagnosticSampleStatus): string {
  switch (status) {
    case 'learning':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
    case 'established':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
    case 'expert':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
  }
}

/**
 * Get DCS quality tier for display
 */
export function getDCSQualityTier(dcs: number): {
  tier: 'low' | 'developing' | 'proficient' | 'strong' | 'exceptional';
  label: string;
  color: string;
} {
  if (dcs >= 85) return { tier: 'exceptional', label: 'Exceptional', color: 'text-emerald-600' };
  if (dcs >= 70) return { tier: 'strong', label: 'Strong', color: 'text-blue-600' };
  if (dcs >= 55) return { tier: 'proficient', label: 'Proficient', color: 'text-sky-600' };
  if (dcs >= 40) return { tier: 'developing', label: 'Developing', color: 'text-amber-600' };
  return { tier: 'low', label: 'Needs Improvement', color: 'text-red-600' };
}
